import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Info, CloudOff, RefreshCw } from "lucide-react";
import api from "../lib/api";
import CoachAccessInviteActionsNew from "./CoachAccessInviteActionsNew";
import { hasCoachProfileContent } from "../lib/coachProfile";
import { resolveInviteErrorMessage } from "../lib/settingsErrorMessages";
import { MoreCardHeader } from "./SettingsMoreCard";

const ALERT = "#B42318";

/** D57's on-page failure shape (round 2 review, S-F1) — slate background, a
 * 3px alert left rule, an info glyph, ink text. the former `AccountProfileCardNew` (#1135)
 * carries its own copy of this same ten-line shape; a shared component
 * wasn't worth adding for two callers. */
function AlertCallout({ children, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "10px 12px",
        background: "#F1F5F9",
        borderLeft: `3px solid ${ALERT}`,
        borderRadius: 10,
        fontSize: 13,
        lineHeight: 1.5,
        color: "#0F172A",
      }}
    >
      <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 }}>
        <Info size={16} color={ALERT} />
      </span>
      <span>{children}</span>
    </div>
  );
}

/**
 * Issue #975 (D186.7-D186.10), round 2 review — Settings A's redrawn
 * "Coach access" card, matching `Settings.dc.html` (design-baseline-round-5).
 * A flag-on SIBLING of `pages/Settings.jsx`'s own `CoachAccessCard`, not an
 * edit to it: the shipped card renders unchanged on the flag-off leg, for a
 * coach, and for an admin (D186.9).
 *
 * THE LIGHT TRUST PATH IS UNCHANGED (issue #975's own hard rule, #138).
 * Every call this card makes is byte-identical to the shipped one, same
 * URLs, same bodies, same order:
 *   - `GET /coach-links`, filtered to non-revoked rows exactly as shipped.
 *   - `GET /coach-profiles/:id`, per link, exactly as shipped
 *     (`loadProfiles`).
 *   - `POST /coach-links` with `{ coach_email: trimmed }` — invite.
 *   - `DELETE /coach-links/:id` — revoke/withdraw, and decline (via
 *     `CoachAccessInviteActionsNew`, itself a sibling of the shipped
 *     `CoachAccessInviteActions`, same two calls).
 *   - `POST /coach-links/:id/accept` — accept (same sibling).
 * Nothing here alters a URL, a body, a header or the accept/decline/
 * revoke ORDER of operations — only the rendering, the states shown, and
 * where a failure is reported.
 *
 * WHAT ACTUALLY CHANGES, PER THE ISSUE'S OWN STATE LIST:
 *   - `coaches-failed-to-load` (#271, round 2 F10) — a failed `GET
 *     /coach-links` shows `settings-coach-links-failed`, the drawn card:
 *     an alert-coloured cloud-off glyph, "Couldn't load your coaches"
 *     (S42), "Nothing has been lost — we just couldn't reach the server.",
 *     and a bordered pill retry — and NEVER the no-coach sentence.
 *   - `no-coach` — zero links (loaded, not failed) shows
 *     `settings-coach-links-empty` (S41).
 *   - `invite-refused` (round 2 F9) — `formatApiErrorDetail(null)` returns
 *     "Something went wrong…", not `null`, so the fallback to "Could not
 *     send invite" is keyed off the SERVER'S OWN body (`detail == null`)
 *     rather than off `formatApiErrorDetail`'s return value, which is
 *     never falsy. Rendered on the page (`settings-coach-invite-error`,
 *     D57) in the shared `AlertCallout` shape, never a toast. A SENT
 *     invite still toasts the server's own ack (#138: the same
 *     enumeration-safe wording for any address).
 *   - `accepting` — while an accept/decline is in flight, both of that
 *     row's buttons wait; a refusal renders under the row in the same
 *     `AlertCallout` shape (via `CoachAccessInviteActionsNew`'s `onError`).
 *   - Remove/withdraw's OWN failure (round 2 S-F2, D57) ALSO renders on the
 *     page now, per row, via the same `rowError` state the accept/decline
 *     pair uses: "Could not remove the coach" / "Could not withdraw the
 *     invite", no toast. The success toasts are unchanged.
 *   - Row labels are visible text (S43: "Remove coach" / "Withdraw
 *     invite"), in D55's ALERT-outlined destructive style (round 2 S-F1) —
 *     precedence: the drawing's own slate hex for these buttons is
 *     superseded by D55's decision, which names Remove coach and Withdraw
 *     invite as destructive actions.
 *   - The waiting (coach-initiated) invite sorts first.
 *   - Badges are navy/slate/outline-navy, never the shipped green/amber.
 *   - The coach's headline and the unverified note render on ONE line,
 *     joined by " · " (round 2 F5, as drawn) — a bespoke line in THIS
 *     component, not the shared `CoachProfileView` (which lays the two out
 *     as separate paragraphs). Journey 2's own `settings-coach-profile-
 *     <id>`/`-unverified` testids are kept on this line so that journey's
 *     assertions hold unchanged.
 */
export default function CoachAccessCardNew() {
  const [links, setLinks] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [rowError, setRowError] = useState({});

  const loadProfiles = (rows) => {
    Promise.all(
      rows.map((l) =>
        api
          .get(`/coach-profiles/${l.coach_id}`)
          .then((r) => [l.coach_id, r.data])
          .catch(() => [l.coach_id, null]),
      ),
    ).then((pairs) => setProfiles(Object.fromEntries(pairs)));
  };

  const loadLinks = () => {
    setLoading(true);
    return api
      .get("/coach-links")
      .then((r) => {
        const rows = r.data.filter((l) => l.status !== "revoked");
        setFailed(false);
        setLinks(rows);
        loadProfiles(rows);
      })
      .catch(() => {
        setFailed(true);
        setLinks([]);
      })
      .finally(() => setLoading(false));
  };

  // Mount only — same reason the shipped card gives (`loadLinks` closes
  // over `loadProfiles` and is re-created every render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLinks(); }, []);

  const sendInvite = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setSending(true);
    setInviteError("");
    try {
      const { data } = await api.post("/coach-links", { coach_email: trimmed });
      // Deliberately generic — see routes/coachLinks.ts's enumeration note,
      // and #138's own acceptance: this UI must never claim to know
      // whether the email matched a coach. Unchanged from the shipped ack.
      toast.success(data.detail);
      setEmail("");
      loadLinks();
    } catch (e) {
      // See resolveInviteErrorMessage's own docstring (#988 N1, round 2
      // review F9) for why this is a guard on the SERVER's body, not on
      // formatApiErrorDetail's always-truthy return value.
      setInviteError(resolveInviteErrorMessage(e.response?.data?.detail));
    }
    setSending(false);
  };

  const revoke = async (link) => {
    setBusyId(link.id);
    setRowError((prev) => ({ ...prev, [link.id]: null }));
    try {
      await api.delete(`/coach-links/${link.id}`);
      toast.success(link.status === "invited" ? "Invite withdrawn" : "Coach access removed");
      loadLinks();
    } catch {
      // Round 2 review, S-F2 (D57): on the page, per row, no toast.
      setRowError((prev) => ({
        ...prev,
        [link.id]: link.status === "invited" ? "Could not withdraw the invite" : "Could not remove the coach",
      }));
    }
    setBusyId(null);
  };

  const isWaiting = (l) => l.status === "invited" && l.initiated_by === "coach";
  // The waiting (coach-initiated) invite sorts first, as drawn. A stable
  // sort — `Array#sort` in every JS engine this app targets — so rows that
  // are equally "waiting" or equally not keep the server's own order.
  const sorted = [...links].sort((a, b) => (isWaiting(a) ? 0 : 1) - (isWaiting(b) ? 0 : 1));

  return (
    <div
      data-testid="settings-coach-access-card"
      style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12 }}
    >
      {/* #1135: the approved board's header (SettingsPlayer.dc.html:
          "Your coach" / "Invite your coach, or remove one."). */}
      <MoreCardHeader icon={Users} title="Your coach" subtitle="Invite your coach, or remove one." />
      {/* F1 (privacy): the consent statement stays as body text, where the action is. */}
      <p data-testid="settings-coach-access-consent" style={{ fontSize: 13, lineHeight: 1.45, color: "var(--nw-ink-lede)", margin: 0 }}>
        The coach will see your name and can accept or decline. They can't see any of your cricket data unless they accept.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (inviteError) setInviteError(""); }}
          placeholder="coach@example.com"
          data-testid="settings-coach-invite-email"
          onKeyDown={(e) => { if (e.key === "Enter") sendInvite(); }}
          style={{ minHeight: 46, padding: "0 12px", background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 10, fontSize: 15, color: "#0F172A" }}
        />
        <button
          type="button"
          onClick={sendInvite}
          disabled={sending || !email.trim()}
          data-testid="settings-coach-invite-send"
          style={{
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 44,
            padding: "0 16px",
            // #1135 (F5, D259): the board's outline style; D195 only forbids gold.
            background: "#FFFFFF",
            color: "#0B2E59",
            fontSize: 14,
            fontWeight: 600,
            borderRadius: 10,
            border: "1px solid #CBD5E1",
            opacity: sending || !email.trim() ? 0.6 : 1,
            cursor: sending || !email.trim() ? "not-allowed" : "pointer",
          }}
        >
          {sending ? "Sending…" : "Invite a coach"}
        </button>
        {/* F9 (round 2 review): the drawn callout frame — slate background,
            red 3px left rule, info icon. */}
        {inviteError && <AlertCallout testId="settings-coach-invite-error">{inviteError}</AlertCallout>}
      </div>

      {/* F10 (round 2 review): the drawn failed-state card — the alert
          cloud-off icon, the second line, and the button-style retry. */}
      {!loading && failed && (
        <div
          data-testid="settings-coach-links-failed"
          style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "20px 16px", background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 12, textAlign: "center" }}
        >
          <div style={{ width: 44, height: 44, borderRadius: 14, background: "#F1F5F9", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CloudOff size={20} color={ALERT} />
          </div>
          <div style={{ fontWeight: 600, fontSize: 14, color: "#0F172A" }}>Couldn't load your coaches</div>
          <div style={{ fontSize: 12, lineHeight: 1.5, color: "#64748B" }}>
            Nothing has been lost — we just couldn't reach the server.
          </div>
          <button
            type="button"
            onClick={loadLinks}
            data-testid="settings-coach-links-failed-retry"
            style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, minHeight: 40, padding: "0 14px", border: "1px solid #CBD5E1", borderRadius: 10, fontSize: 13, fontWeight: 600, color: "#0B2E59", background: "#FFFFFF", cursor: "pointer" }}
          >
            <RefreshCw size={14} />
            Try again
          </button>
        </div>
      )}

      {!loading && !failed && sorted.length === 0 && (
        <p data-testid="settings-coach-links-empty" style={{ fontSize: 13, color: "#64748B", margin: 0 }}>
          No coach can see your record yet. Send an invite above; nothing is shared until they accept.
        </p>
      )}

      {!loading && !failed && sorted.length > 0 && (
        <div data-testid="settings-coach-links" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sorted.map((l) => {
            const waiting = isWaiting(l);
            const badgeText = l.status === "active" ? "Active" : waiting ? "Invited you" : "Invite pending";
            const badgeStyle =
              l.status === "active"
                ? { background: "#E8F1FB", color: "#0B2E59" }
                : waiting
                  ? { background: "#FFFFFF", border: "1px solid #0B2E59", color: "#0B2E59" }
                  : { background: "#F1F5F9", color: "#475569" };
            const profile = profiles[l.coach_id];

            return (
              <div
                key={l.id}
                data-testid={`settings-coach-link-${l.id}`}
                style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12, background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 10 }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#0F172A" }}>{l.coach_display_name || l.coach_email}</div>
                    <div style={{ fontSize: 12, color: "#64748B" }}>{l.coach_email}</div>
                  </div>
                  <span
                    data-testid={`settings-coach-link-status-${l.id}`}
                    style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", fontSize: 11, fontWeight: 700, borderRadius: 999, whiteSpace: "nowrap", ...badgeStyle }}
                  >
                    {badgeText}
                  </span>
                </div>

                {waiting && (
                  <p data-testid={`settings-coach-invite-consent-${l.id}`} style={{ fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-lede)", margin: 0 }}>
                    This coach has invited you. They can't see any of your cricket data unless you accept.
                  </p>
                )}
                {waiting ? (
                  <CoachAccessInviteActionsNew
                    link={l}
                    onDone={loadLinks}
                    onError={(msg) => setRowError((prev) => ({ ...prev, [l.id]: msg }))}
                  />
                ) : (
                  /* D55 (round 2 review, S-F1): the alert-outlined trigger —
                     precedence over the drawing's own slate hex, since D55
                     names Remove coach/Withdraw invite as destructive. */
                  /* #1394 (D221 G4, D354): the button is a transparent 44px tap box and the
                     40px outlined control is drawn inside it. The margin pulls the margin box
                     back to the control's own 40px, so the row keeps its height and the
                     control stays where it was drawn. The button's own outline is dropped
                     (it would trace the invisible 44px box); a keyboard-focused control gets
                     a 2px ring on the drawn control instead. */
                  <button
                    type="button"
                    onClick={() => revoke(l)}
                    disabled={busyId === l.id}
                    data-testid={`settings-coach-link-revoke-${l.id}`}
                    className="group outline-none"
                    style={{
                      alignSelf: "flex-end",
                      display: "inline-flex",
                      alignItems: "center",
                      minHeight: "var(--nw-size-tap)",
                      marginBlock: "calc((40px - var(--nw-size-tap)) / 2)",
                      padding: 0,
                      border: "none",
                      background: "transparent",
                      cursor: busyId === l.id ? "not-allowed" : "pointer",
                      opacity: busyId === l.id ? 0.6 : 1,
                    }}
                  >
                    <span
                      className="group-focus-visible:ring-2 group-focus-visible:ring-[#0B2E59]"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        minHeight: 40,
                        padding: "0 12px",
                        border: `1px solid ${ALERT}`,
                        borderRadius: 10,
                        fontSize: 13,
                        fontWeight: 600,
                        color: ALERT,
                        background: "#FFFFFF",
                      }}
                    >
                      {l.status === "invited" ? "Withdraw invite" : "Remove coach"}
                    </span>
                  </button>
                )}

                {/* S-F2 (round 2 review, D57): accept/decline AND
                    remove/withdraw refusals share this one on-page slot,
                    per row — no toast for any of the four. */}
                {rowError[l.id] && (
                  <AlertCallout testId={`settings-coach-invite-actions-error-${l.id}`}>{rowError[l.id]}</AlertCallout>
                )}

                {/* F5 (round 2 review): the coach's headline and the
                    unverified note on ONE line, joined by " · ", as drawn —
                    a bespoke line rather than the shared `CoachProfileView`
                    (which lays them out as separate paragraphs).
                    `settings-coach-profile-<id>`/`-unverified` are kept so
                    journey 2's own assertions (the pending-invite state)
                    hold unchanged. */}
                {hasCoachProfileContent(profile) && (
                  <div
                    data-testid={`settings-coach-profile-${l.id}`}
                    style={{ fontSize: 12, lineHeight: 1.5, color: "#475569" }}
                  >
                    {profile?.headline ? `${profile.headline} · ` : ""}
                    <span data-testid={`settings-coach-profile-${l.id}-unverified`}>
                      Provided by the coach. We haven't verified it.
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
